import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, FileText, CheckCircle, 
  DollarSign, MessageSquare, CreditCard,
  User, Send, BellRing
} from 'lucide-react';
import { Badge } from '../../ui/badge';

const PatientBillingSlide = () => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setStep((prev) => (prev + 1) % 4);
    }, 4000); // 4 seconds per step
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="h-full w-full bg-slate-900 flex items-center justify-center p-12 relative overflow-hidden text-white">
        {/* Abstract Background */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

        {/* Floating Agent Header */}
        <div className="absolute top-8 left-8 lg:left-12 z-50 flex items-center gap-4 bg-white/10 backdrop-blur-md p-2 pr-6 rounded-full border border-white/10 shadow-lg">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-slate-700 shadow-sm">
                 <img src="/assets/kianna.png" alt="Kianna" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
                <h3 className="text-lg font-bold text-white leading-none">Kianna</h3>
                <p className="text-xs text-blue-400 font-bold uppercase tracking-wide">Patient Billing</p>
            </div>
        </div>

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-12 relative z-10 pt-0">
            {/* Left Side: Context - Expanded Width */}
            <div className="w-[45%] flex flex-col justify-center space-y-6">
                <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
                    She calls patients to <span className="text-blue-400">collect balances</span> automatically.
                </h2>
                
                <p className="text-lg text-slate-300 leading-relaxed">
                   Kianna generates invoices and runs intelligent voice & SMS campaigns to collect payments and update your ledger.
                </p>

                <div className="space-y-3 pt-4">
                    <ProcessStep 
                        icon={FileText} 
                        title="1. Generate Statement" 
                        desc="Creates clear, easy-to-read invoices."
                        active={step === 0}
                        color="blue"
                    />
                    <ProcessStep 
                        icon={Phone} 
                        title="2. Intelligent Outreach" 
                        desc="Calls & texts patients to collect payment."
                        active={step === 1}
                        color="indigo"
                    />
                     <ProcessStep 
                        icon={CreditCard} 
                        title="3. Collect & Post" 
                        desc="Processes card and updates ledger."
                        active={step === 2}
                        color="emerald"
                    />
                </div>
            </div>

            {/* Right Side: Interactive Visualization - Reduced Width */}
            <div className="w-[55%] h-full flex items-center justify-center">
                <div className="w-full max-w-4xl h-[600px] relative bg-slate-950/50 rounded-3xl border border-slate-800 p-8 flex flex-col items-center justify-center overflow-hidden">
                    
                    {/* Background Grid */}
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:3rem_3rem]" />

                    {/* Central Patient Phone Mockup */}
                    <div className="relative z-20 flex gap-8 items-center">
                        
                        {/* Agent Chat Bubbles (Left) */}
                        <AnimatePresence mode="wait">
                            {step === 1 && (
                                <motion.div 
                                    initial={{ x: -50, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    exit={{ x: -50, opacity: 0 }}
                                    className="hidden lg:flex flex-col items-end gap-4"
                                >
                                    <div className="bg-blue-600 rounded-2xl rounded-br-none p-4 max-w-[250px] shadow-lg shadow-blue-900/20 relative">
                                        <p className="text-sm font-medium">Hi Sarah, this is Kianna from Dr. Smith's office. You have a balance of $45.50 after insurance paid.</p>
                                        <div className="absolute -bottom-2 -right-2 w-4 h-4 bg-blue-600 transform rotate-45" />
                                    </div>
                                    <div className="bg-blue-600 rounded-2xl rounded-br-none p-4 max-w-[250px] shadow-lg shadow-blue-900/20 relative delay-300">
                                        <p className="text-sm font-medium">Would you like to take care of that with the card ending in 4242?</p>
                                        <div className="absolute -bottom-2 -right-2 w-4 h-4 bg-blue-600 transform rotate-45" />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Phone Frame */}
                        <div className="w-[300px] h-[550px] bg-slate-900 rounded-[3rem] border-8 border-slate-800 shadow-2xl relative overflow-hidden flex flex-col">
                            {/* Top Bar */}
                            <div className="h-14 bg-slate-800/50 flex items-center justify-center border-b border-white/5 relative z-10">
                                <div className="w-20 h-6 bg-black rounded-full absolute top-2" />
                                <span className="text-xs text-slate-400 mt-2">10:42 AM</span>
                            </div>

                            {/* Screen Content */}
                            <div className="flex-1 relative bg-slate-950 p-4">
                                <AnimatePresence mode="wait">
                                    {step === 0 && (
                                        <motion.div 
                                            key="invoice"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="h-full flex flex-col items-center justify-center space-y-4"
                                        >
                                            <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-2">
                                                <FileText className="w-8 h-8 text-slate-400" />
                                            </div>
                                            <h3 className="text-lg font-bold">New Statement</h3>
                                            <div className="w-full bg-white rounded-xl p-4 text-slate-900 shadow-lg">
                                                <div className="flex justify-between border-b border-slate-100 pb-2 mb-2">
                                                    <span className="font-bold">Total Due</span>
                                                    <span className="font-bold text-red-500">$45.50</span>
                                                </div>
                                                <div className="space-y-2 text-xs text-slate-500">
                                                    <div className="flex justify-between">
                                                        <span>Visit Date</span>
                                                        <span>Oct 12</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span>Procedure</span>
                                                        <span>Exam</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-blue-400">
                                                <BellRing className="w-4 h-4" /> Generating Invoice...
                                            </div>
                                        </motion.div>
                                    )}

                                    {step === 1 && (
                                        <motion.div 
                                            key="call"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="h-full flex flex-col items-center justify-center relative"
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-slate-950 z-0" />
                                            
                                            <div className="z-10 text-center space-y-6">
                                                <div className="relative">
                                                    <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-slate-800 mx-auto">
                                                        <img src="/assets/kianna.png" alt="Kianna" className="w-full h-full object-cover" />
                                                    </div>
                                                    <motion.div 
                                                        animate={{ scale: [1, 1.2, 1] }}
                                                        transition={{ repeat: Infinity, duration: 2 }}
                                                        className="absolute inset-0 border-2 border-blue-500 rounded-full"
                                                    />
                                                    <motion.div 
                                                        animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                                                        transition={{ repeat: Infinity, duration: 2, delay: 0.2 }}
                                                        className="absolute inset-0 border border-blue-500/50 rounded-full"
                                                    />
                                                </div>
                                                
                                                <div>
                                                    <h3 className="text-2xl font-bold">Dr. Smith's Office</h3>
                                                    <p className="text-blue-400">Kianna (AI Agent)</p>
                                                    <p className="text-sm text-slate-500 mt-2">00:42</p>
                                                </div>

                                                <div className="flex gap-8 justify-center mt-8">
                                                    <div className="w-14 h-14 bg-red-500 rounded-full flex items-center justify-center shadow-lg shadow-red-500/30">
                                                        <Phone className="w-6 h-6 fill-white rotate-[135deg]" />
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}

                                    {step >= 2 && (
                                        <motion.div 
                                            key="success"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="h-full flex flex-col items-center justify-center space-y-6"
                                        >
                                            <motion.div 
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                type="spring"
                                                className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center shadow-xl shadow-green-500/30"
                                            >
                                                <CheckCircle className="w-10 h-10 text-white" />
                                            </motion.div>
                                            
                                            <div className="text-center">
                                                <h3 className="text-xl font-bold">Payment Successful</h3>
                                                <p className="text-slate-400 text-sm mt-1">Transaction #88291</p>
                                            </div>

                                            <div className="w-full bg-slate-900 rounded-xl p-4 border border-slate-800">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center">
                                                        <DollarSign className="w-4 h-4 text-green-400" />
                                                    </div>
                                                    <div className="text-left">
                                                        <div className="text-xs font-bold text-slate-300">Ledger Updated</div>
                                                        <div className="text-[10px] text-slate-500">Balance: $0.00</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>

                        {/* Patient Response Side (Right) */}
                        <AnimatePresence mode="wait">
                            {step === 1 && (
                                <motion.div 
                                    initial={{ x: 50, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    exit={{ x: 50, opacity: 0 }}
                                    transition={{ delay: 0.5 }}
                                    className="hidden lg:flex flex-col items-start gap-4"
                                >
                                    <div className="bg-slate-700 rounded-2xl rounded-bl-none p-4 max-w-[250px] shadow-lg relative mt-16">
                                        <p className="text-sm font-medium">Oh, I thought I paid that last time. Sure, go ahead and run the card.</p>
                                        <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-slate-700 transform rotate-45" />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                    </div>

                </div>
            </div>
        </div>
    </div>
  );
};

const ProcessStep = ({ icon: Icon, title, desc, active, color = "blue" }) => {
    const activeClass = color === "blue" ? "bg-blue-500/10 border-blue-500/30 shadow-lg shadow-blue-900/10" : 
                        color === "indigo" ? "bg-indigo-500/10 border-indigo-500/30 shadow-lg shadow-indigo-900/10" :
                        "bg-emerald-500/10 border-emerald-500/30 shadow-lg shadow-emerald-900/10";
    
    const iconClass = color === "blue" ? "bg-blue-500 text-white" :
                      color === "indigo" ? "bg-indigo-500 text-white" :
                      "bg-emerald-500 text-white";

    const textClass = color === "blue" ? "text-blue-200" :
                      color === "indigo" ? "text-indigo-200" :
                      "text-emerald-200";

    return (
        <div className={`flex items-start gap-4 p-4 rounded-xl transition-all duration-500 border ${active ? activeClass : 'bg-transparent border-transparent opacity-50'}`}>
            <div className={`p-2 rounded-lg mt-0.5 transition-colors duration-500 ${active ? iconClass : 'bg-slate-800 text-slate-500'}`}>
                <Icon className="w-5 h-5" />
            </div>
            <div>
                <h4 className={`font-bold text-lg transition-colors duration-500 ${active ? 'text-white' : 'text-slate-400'}`}>{title}</h4>
                <p className="text-sm text-slate-400 leading-snug">{desc}</p>
            </div>
        </div>
    );
};

export default PatientBillingSlide;
